// src/pages/admin/HomeImageManager.jsx
import React, { useState, useEffect } from 'react';
import { AdminHomeImageAPI } from '../services/api';
import { 
  Image, Upload, RefreshCw, CheckCircle, AlertCircle, 
  Eye, Loader2, Edit2, Save, X
} from 'lucide-react';

const HomeImageManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [message, setMessage] = useState(null);
  const [editingDescription, setEditingDescription] = useState(null);
  const [descriptionValue, setDescriptionValue] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await AdminHomeImageAPI.getAllImages();
      if (response.success) {
        setImages(response.data);
      } else {
        setMessage({ type: 'error', text: response.error || 'Failed to load images' });
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      setMessage({ type: 'error', text: 'Failed to load images. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (key, file) => {
    try {
      setUploading(prev => ({ ...prev, [key]: true }));
      setMessage(null);
      
      const response = await AdminHomeImageAPI.uploadImage(key, file);
      
      if (response.success) {
        await fetchImages();
        setMessage({ 
          type: 'success', 
          text: '✅ Image uploaded successfully!' 
        });
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ 
          type: 'error', 
          text: response.error || 'Upload failed' 
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Upload failed. Please try again.' 
      });
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUpdateDescription = async (key, description) => {
    try {
        setSavingDescription(true);
        setMessage(null);
        
        console.log('Updating description for:', key, 'Description:', description);
        
        // Use the new updateDescription endpoint
        const response = await AdminHomeImageAPI.updateDescription(key, description);
        
        console.log('Update response:', response);
        
        if (response.success) {
            await fetchImages();
            setMessage({ 
                type: 'success', 
                text: '✅ Description updated successfully!' 
            });
            setTimeout(() => setMessage(null), 3000);
        } else {
            setMessage({ 
                type: 'error', 
                text: response.error || 'Failed to update description' 
            });
        }
    } catch (error) {
        console.error('Update error:', error);
        setMessage({ 
            type: 'error', 
            text: error.message || 'Failed to update description. Please try again.' 
        });
    } finally {
        setSavingDescription(false);
        setEditingDescription(null);
        setDescriptionValue('');
    }
};

  const getImageLabel = (key) => {
    const labels = {
      'carousel_image_1': 'Carousel Image 1',
      'carousel_image_2': 'Carousel Image 2',
      'carousel_image_3': 'Carousel Image 3',
      'carousel_image_4': 'Carousel Image 4',
      'banner_1': 'Promotional Banner 1',
      'banner_2': 'Featured Players Banner',
      'banner_3': 'World Cup 2026 Banner',
      'welcome_image': 'Welcome Banner',
      'agent_modal_image': 'Agent Modal Image'
    };
    return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getImageDescription = (key) => {
    const descriptions = {
      'carousel_image_1': 'First slide in the homepage carousel',
      'carousel_image_2': 'Second slide in the homepage carousel',
      'carousel_image_3': 'Third slide in the homepage carousel',
      'carousel_image_4': 'Fourth slide in the homepage carousel',
      'banner_1': 'Main promotional banner shown on the homepage',
      'banner_2': 'Featured players and promotions banner',
      'banner_3': 'World Cup 2026 promotional banner',
      'welcome_image': 'Welcome banner shown at the top of the page',
      'agent_modal_image': 'Image shown in the agent modal popup'
    };
    return descriptions[key] || 'Home page image';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
          <p className="text-charcoal-gray">Loading images...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
            🖼️ Home Page Images
          </h1>
          <p className="text-charcoal-gray mt-1">Manage images displayed on the home page</p>
        </div>
        <button
          onClick={fetchImages}
          className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
            : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Images Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.map((image) => (
          <div key={image.id} className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 transition-colors">
            {/* Image Preview */}
            <div className="relative aspect-video bg-gray-900 overflow-hidden">
              <img
                src={image.full_url}
                alt={image.setting_key}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 400 225"%3E%3Crect width="400" height="225" fill="%231a1a2e"%3E%3C/rect%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23666" font-size="16" font-family="sans-serif" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                }}
              />
              <div className="absolute top-3 right-3">
                <span className="bg-black/70 text-white text-xs px-2.5 py-1 rounded-full backdrop-blur-sm">
                  {image.setting_key}
                </span>
              </div>
            </div>

            {/* Image Info */}
            <div className="p-4 space-y-3">
              <div>
                <h3 className="text-white font-semibold text-lg">
                  {getImageLabel(image.setting_key)}
                </h3>
                <p className="text-charcoal-gray text-sm">
                  {getImageDescription(image.setting_key)}
                </p>
              </div>

              {/* File Info */}
              <div className="bg-gray-800/30 rounded-lg p-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-charcoal-gray">Filename:</span>
                  <span className="text-white font-mono truncate ml-2">
                    {image.setting_value}
                  </span>
                </div>
              </div>

              {/* Description - Updated */}
              <div className="bg-gray-800/20 rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-charcoal-gray">Description</p>
                    {editingDescription === image.id ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="text"
                          value={descriptionValue}
                          onChange={(e) => setDescriptionValue(e.target.value)}
                          className="flex-1 bg-gray-700 text-white text-sm px-3 py-1.5 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                          placeholder="Enter description..."
                          autoFocus
                          disabled={savingDescription}
                        />
                        <button
                          onClick={() => handleUpdateDescription(image.setting_key, descriptionValue)}
                          disabled={savingDescription}
                          className="p-1.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {savingDescription ? (
                            <Loader2 className="w-4 h-4 text-white animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 text-white" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingDescription(null);
                            setDescriptionValue('');
                          }}
                          className="p-1.5 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm text-gray-300 flex-1">
                          {image.description || 'No description'}
                        </p>
                        <button
                          onClick={() => {
                            setEditingDescription(image.id);
                            setDescriptionValue(image.description || '');
                          }}
                          className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-3 h-3 text-gray-300" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Upload Button */}
              <div className="flex gap-2">
                <label className="flex-1 cursor-pointer">
                  <div className={`w-full py-2.5 px-4 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                    uploading[image.setting_key]
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white hover:scale-[1.02]'
                  }`}>
                    {uploading[image.setting_key] ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Change Image
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleUpload(image.setting_key, e.target.files[0]);
                      }
                      e.target.value = '';
                    }}
                    disabled={uploading[image.setting_key]}
                  />
                </label>
              </div>

              {/* Preview Link */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-800/50">
                <span className="text-xs text-charcoal-gray">
                  JPG, PNG, GIF, WEBP • Max 5MB
                </span>
                <a
                  href={image.full_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-400 hover:text-sky-300 text-xs flex items-center gap-1 transition-colors"
                >
                  <Eye className="w-3 h-3" />
                  View Full
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {images.length === 0 && (
        <div className="text-center py-12 bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl">
          <Image className="w-16 h-16 text-charcoal-gray mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Images Found</h3>
          <p className="text-charcoal-gray">No home page images have been configured yet.</p>
        </div>
      )}
    </div>
  );
};

export default HomeImageManager;